import { t } from "@lingui/core/macro";
import { useLinkProps } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { add, format, parse } from "date-fns";
import { uniqBy } from "lodash";
import ordinal from "ordinal";
import { ComponentProps, useMemo } from "react";
import { useFragment } from "react-relay";

import {
  MyScheduleCard,
  MyScheduleCardProps,
} from "@whoosh/design-system/src/apps/PatronApp/components/MyScheduleCard/MyScheduleCard";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { dateUtils } from "@whoosh/utilities";
import { getNounOverride } from "@whoosh/utilities/src/FormatNounOverride/getNounOverride";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { useHasUnpublishedRequestRestrictions } from "../../screens/BookingDetail/NewPatronBookingFlow/components/BookingSummary/components/BookingRequest/components/AvailableTeeTimes/hooks/useHasUnpublishedRequestRestrictions";
import { useShouldPresentBookingAsLockedRequest } from "../../screens/BookingDetail/NewPatronBookingFlow/components/BookingSummary/components/BookingRequest/components/AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { useFlags } from "../../utils/launchDarkly";
import { BookingCard_Booking$key } from "./__generated__/BookingCard_Booking.graphql";
import {
  getFallbackDepartmentByFacilityType,
  isKnownDepartmentType,
} from "./utils";

type BoxProps = ComponentProps<typeof Box>;
type BoxWidthPropType = BoxProps["width"];

type BookingCardProps = {
  BookingCardRef: BookingCard_Booking$key;
  width?: BoxWidthPropType;
};
export const BookingCard = ({
  BookingCardRef,
  width = 305,
}: BookingCardProps) => {
  const booking = useFragment(BookingCardFragmentString, BookingCardRef);
  const isLesson = booking.__typename === "LessonBooking";
  const isDining = booking.__typename === "DiningBooking";
  const isRequest = booking.startSlot?.__typename === "QueueSlot";
  const isLockedRequest = useShouldPresentBookingAsLockedRequest(booking);
  const isRequestPresentation = isRequest || isLockedRequest;
  const { waitlistRestrictions, requestConfigs } = useFlags();
  const hasUnpublishedRequestRestrictions =
    useHasUnpublishedRequestRestrictions(booking.startSlot ?? null);
  const requestRestrictionStatus =
    booking.requestScheduleBlockingScope ?? undefined;

  const linkProps = useLinkProps({
    screen: "BookingSummary",
    params: {
      slotOrBookingId: booking.id,
    },
  });

  const isToday = dateUtils.useIsToday(booking.startSlot?.agenda.date || "");
  const dateObj = booking.startSlot?.agenda.date
    ? parse(booking.startSlot?.agenda.date, "yyyy-MM-dd", new Date())
    : null;

  const formattedDate = isToday
    ? "Today"
    : dateObj
    ? format(dateObj, "EEE, MMM do")
    : "";

  const showCourseName =
    (!!((booking.startSlot?.agenda?.facility?.courses?.count ?? 0) > 1) &&
      !!booking.startSlot?.course) ||
    isDining;

  const courseName = isDining
    ? booking.roomName
    : booking.startSlot?.course?.name;
  const courseBucketName = booking.startSlot?.course?.courseBucket?.name;

  const holeOffset = booking.startSlot?.holeOffset;
  const groupLabel = booking.startSlot?.groupLabel;

  const holeOffsetGroupLabel =
    holeOffset && holeOffset > 0
      ? groupLabel
        ? `: ${ordinal((holeOffset || 0) + 1)}(${groupLabel}) Hole`
        : `: ${ordinal((holeOffset || 0) + 1)} Hole`
      : null;

  const instructor = booking.instructors?.edges?.length
    ? booking.instructors.edges[0]?.node
    : null;
  const facilityName = booking.startSlot?.agenda.facility.name;

  const locationText = useMemo(() => {
    if (showCourseName) {
      const location = holeOffsetGroupLabel
        ? `${courseName}${holeOffsetGroupLabel}`
        : courseBucketName
        ? `${courseBucketName}: ${courseName}`
        : `${courseName}`;

      return isLesson
        ? courseName
          ? location
          : t`Location Pending`
        : location || " ";
    } else {
      return facilityName;
    }
  }, [
    courseBucketName,
    courseName,
    facilityName,
    holeOffsetGroupLabel,
    isLesson,
    showCourseName,
  ]);

  const facilityType = booking.startSlot?.agenda.facility.type;
  const departmentType = booking.startSlot?.agenda.facility?.department?.type;
  const fallbackDepartment = getFallbackDepartmentByFacilityType(facilityType);
  const normalizedDepartmentType = isKnownDepartmentType(departmentType)
    ? departmentType
    : fallbackDepartment.departmentType;
  const normalizedDepartmentName =
    booking.startSlot?.agenda.facility?.department?.name?.trim() ||
    fallbackDepartment.departmentName;

  const startTime = isRequestPresentation
    ? booking.request?.time
    : booking.startSlot?.time;

  const endTime =
    facilityType !== "GOLF_COURSE" &&
    (booking.slots?.edges?.length ?? 0) > 0 &&
    booking.slots?.edges
      ? booking.slots.edges[booking.slots.edges.length - 1]?.node?.time
      : null;

  const endTimeObj = endTime ? parse(endTime, "HH:mm:ss", new Date()) : null;
  const endTimeWithDurationOfFinalSlotAdded = endTimeObj
    ? add(endTimeObj, { minutes: 15 })
    : null;

  const endTimeHHMM = endTimeWithDurationOfFinalSlotAdded
    ? format(endTimeWithDurationOfFinalSlotAdded, "HH:mm:ss")
    : null;

  const endTimeFormatted = endTimeHHMM ? formatHHMM(endTimeHHMM) : null;

  const lessonEndTime =
    isLesson && startTime && booking.durationMins
      ? formatHHMM(
          format(
            add(parse(startTime, "HH:mm:ss", new Date()), {
              minutes: booking.durationMins,
            }),
            "HH:mm:ss"
          )
        )
      : null;

  const timeRangeEndString = isLesson ? lessonEndTime : endTimeFormatted;

  const timeRange = startTime
    ? `${dateUtils.formatHHMM(startTime)}${
        timeRangeEndString !== null ? ` - ${timeRangeEndString}` : ``
      }`
    : undefined;

  const subTitle = isLesson
    ? instructor
      ? `with ${instructor.user.fullName}`
      : booking.startSlot?.agenda.facility.lessonsDisplayName || "Lesson"
    : facilityType === "GOLF_COURSE" && departmentType === "GOLF"
    ? `Tee Time`
    : booking.startSlot?.agenda.facility.name || normalizedDepartmentName;

  const slotPlayersAvatarData =
    booking?.startSlot?.bookings?.edges?.flatMap(
      (b) =>
        b?.node?.rounds?.edges?.map((round) => {
          return {
            id: round?.node?.id,
            profileImageId: round?.node?.player?.profileImage?.publicId,
            firstName: round?.node?.player?.firstName ?? null,
            lastName: round?.node?.player?.lastName ?? null,
          };
        }) ?? []
    ) ?? [];

  const bookingPlayersAvatarData =
    booking.rounds?.edges?.map((round) => {
      return {
        id: round?.node?.id,
        profileImageId: round?.node?.player?.profileImage?.publicId,
        firstName: round?.node?.player?.firstName ?? null,
        lastName: round?.node?.player?.lastName ?? null,
      };
    }) ?? [];

  const playerAvatarData = uniqBy(
    [...slotPlayersAvatarData, ...bookingPlayersAvatarData],
    "id"
  );

  //We hide the draft bookings here as they will only hang around if orphaned.
  //They will be removed by a job on the backend eventually
  const isDraft = booking?.status === "DRAFT";

  const supportsPublic =
    booking?.startSlot?.agenda?.facility?.club?.supportsPublic ?? false;
  const clientCanFulfillRequests = supportsPublic
    ? booking?.startSlot?.agenda?.facility?.publicBookingPermissionSet
        ?.clientCanFulfillRequests ?? false
    : booking?.startSlot?.agenda?.facility?.privateBookingPermissionSet
        ?.clientCanFulfillRequests ?? false;

  const hasAvailableTimes =
    (booking?.fullfillableSlots?.length ?? 0) > 0 &&
    !!waitlistRestrictions &&
    clientCanFulfillRequests &&
    (!requestConfigs || !hasUnpublishedRequestRestrictions);

  const lessonNoun = getNounOverride(
    booking?.startSlot?.agenda?.facility?.copyOverrides?.lesson?.singular,
    t`Lesson`
  );
  const recurringSeriesIndex = booking.seriesIndex ?? 0;
  const recurringSeriesCount = booking.seriesCount ?? 0;
  const shouldShowRecurringTitleSuffix =
    recurringSeriesIndex > 0 && recurringSeriesCount > 1;
  const recurringTitleSuffix = shouldShowRecurringTitleSuffix
    ? t` • ${ordinal(recurringSeriesIndex)} of ${recurringSeriesCount}`
    : undefined;
  const formattedBooking: MyScheduleCardProps = {
    title: `${
      isRequestPresentation
        ? isLesson
          ? t`${lessonNoun} Request`
          : t`Request`
        : isLesson
        ? lessonNoun
        : t`Booking`
    }${recurringTitleSuffix ?? ""}`,
    subTitle,
    date: formattedDate,
    cardColor: "C",
    departmentType: normalizedDepartmentType,
    departmentName: normalizedDepartmentName,
    courseNames: isRequestPresentation ? t`Waitlist` : locationText,
    onPress: () => linkProps.onPress(),
    signupStatus: isRequestPresentation ? ("REQUEST" as "REQUEST") : undefined,
    playerAvatarData,
    timeRange: timeRange,
    hasAvailableTimes,
    requestRestrictionStatus,
    testID: "booking-card",
  };

  return isDraft ? null : (
    <Box width={width} flex={1}>
      <MyScheduleCard {...formattedBooking} />
    </Box>
  );
};

export const BookingCardFragmentString = graphql`
  fragment BookingCard_Booking on Booking {
    __typename
    id
    requestScheduleBlockingScope
    ...useShouldPresentBookingAsLockedRequest_booking
    rounds {
      edges {
        node {
          id
          player {
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
    startSlot {
      __typename
      ...useHasUnpublishedRequestRestrictions_slot
      agenda {
        date
        facility {
          club {
            supportsPublic
          }
          privateBookingPermissionSet {
            clientCanFulfillRequests
          }
          publicBookingPermissionSet {
            clientCanFulfillRequests
          }
          type
          lessonsDisplayName
          copyOverrides {
            lesson {
              singular
            }
          }
          department {
            type
            name
          }
          name
          courses {
            count
          }
        }
      }
      ... on TimeSlot {
        time
        groupLabel
        holeOffset
        bookings {
          edges {
            node {
              rounds {
                edges {
                  node {
                    id
                    player {
                      firstName
                      lastName
                      profileImage {
                        # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                        publicId: renderingKey
                      }
                    }
                  }
                }
              }
            }
          }
        }
        course {
          name
          courseBucket {
            name
          }
        }
      }
      ... on BucketTimeSlot {
        time
      }
    }
    ... on StandardBooking {
      seriesIndex
      seriesCount
      fullfillableSlots {
        id
      }
      status
      slots {
        edges {
          node {
            ... on TimeSlot {
              time
            }
          }
        }
      }
      request {
        time
      }
    }
    ... on LessonBooking {
      seriesIndex
      seriesCount
      durationMins
      request {
        time
      }
      instructors {
        edges {
          node {
            id
            user {
              fullName
            }
          }
        }
      }
    }
    ... on DiningBooking {
      roomName
    }
  }
`;
